
import { Component, Prop, Watch } from 'vue-property-decorator'
import Axios from 'axios'
import { email, minLength, required, helpers } from 'vuelidate/lib/validators'
import { gtag, gtagSendToIdQuoteRequest } from "../core/constants";
import { ANTIFORGERY_INPUT_NAME, BASE_URL } from '../helpers/constants'
import { ICartNL } from '../helpers/types'
import BasicShoppingCart from './basic-shopping-cart.vue'

const isNlPostCode = (value: string) => !helpers.req(value) || /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i.test(value)

@Component({
  validations: {
    cartData: {
      ProjectInformation:{
        Organization: { required, minLength: minLength(1) },
        ProjectName: { required, minLength: minLength(1) },
        ContactPersonName: { required, minLength: minLength(1) },
        PhoneNumber: { required, minLength: minLength(1) },
        EmailAddress: { required, minLength: minLength(1), email },
        PostalCode: { isNlPostCode }
      },
      QuotationRequestData: {
        Name: { required, minLength: minLength(1) },
        EmailAddress: { required, minLength: minLength(1), email }
      }
    },
    terms: { checked: val => val }
  }
})

export default class ShoppingCart extends BasicShoppingCart {
  @Prop() cartItems: any
  @Prop() cartId: any

  loading = false
  cartData: ICartNL | null = null
  openCommentIndex: number | null = null
  shoppingCartComponentKey: 0
  minusActive = false
  plusActive = false

  terms = false
  files: any = []
  buttonLoading = false


  toggleComment(index: number): void {
    this.openCommentIndex = this.openCommentIndex === index ? null : index
  }

  hasComment(comment: any): boolean {
    if (comment) {
      return true
    }
    return false
  }

  async commitComment(item: any, itemId: string): Promise<void>  {
    if(!this.cartData) return
    const comment = item.target.value

    try {
      const res = await Axios({
        method: 'put',
        url: `${BASE_URL}/cart/items/comment`,
        data: {
          lineItemId: itemId,
          comment: comment
        }
      })
      if (res) {
        const tempItems = this.$store.state.cart.items
        tempItems.find((el: any) => {
          if (el.id ===  itemId) {
            el.comment = comment
            return true
          }
          return false
        })
        this.$store.commit('cart/setItems', tempItems)
      } else throw new Error()
    } catch (error) {
      this.$store.commit('notifications/add', { message: this.$options?.filters?.t('downloadFormErrorMessage')})
    }
  }

  async finishOrder(): Promise<void> {
    this.buttonLoading = true
    try {
      const antiForgeryInput: HTMLInputElement | null = document.querySelector(`input[name=${ANTIFORGERY_INPUT_NAME}]`)
      const bodyFormData = this.toFormData()
      if(antiForgeryInput) bodyFormData.set(ANTIFORGERY_INPUT_NAME, antiForgeryInput.value)

      await Axios.post(
        `${BASE_URL}/quoterequests/nl/submit`,
        bodyFormData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )

      this.$store.commit('cart/setItemsCount', 0)
      this.$store.commit('cart/setItems', [])
      this.$store.dispatch('cart/getShoppingCartCount')
      this.enabledConfirmation = true
      this.$store.commit('cart/clearCartData')
      this.activeStep++
      window.scrollTo(0, 0)
      this.sendGaCheckoutEvent(this.activeStep + 1, this.cartId, this.cartItems)
      this.sendGaPurchaseEvent(this.cartId, this.cartItems)


      if(window.CURRENT_STORE === 'HakronNL') {
        /* eslint-disable @typescript-eslint/camelcase */
        gtag('event', 'conversion', {
          send_to: gtagSendToIdQuoteRequest
        });
      }

    } catch (error) {
      // the quote request itself was submitted unless the request failed,
      // so only a failing request may be reported to the visitor
      if (Axios.isAxiosError(error)) {
        this.$store.commit('notifications/add', { message: this.$options?.filters?.t('sendCartError') })
      }
    } finally {
      this.buttonLoading = false
    }
  }

  toFormData(): FormData {
    const bodyFormData = new FormData();

    const projectInfoKey = Object.keys(this.cartData!.ProjectInformation)
    projectInfoKey.forEach(key => {
      const data = this.cartData!.ProjectInformation as any
      bodyFormData.set('request.ProjectInformation.' + key, data[key])
    })

    const quotationReqKeys = Object.keys(this.cartData!.QuotationRequestData)
    quotationReqKeys.forEach(key => {
      const data = this.cartData!.QuotationRequestData as any
      bodyFormData.set('request.QuotationRequestData.' + key, data[key])
    })

    if(this.cartData!.Attachments){
      this.cartData!.Attachments!.forEach((item, index) => {

        if(item.AttachmentData){
          bodyFormData.set(`request.Attachments[${index}].OrderLineId`,  item.OrderLineId!)
          if(item.AttachmentData) bodyFormData.set(`request.Attachments[${index}].AttachmentData`, item.AttachmentData)
        }
      })
    }

    return bodyFormData
  }

  updateAttachment(item: any, itemId: string, fileIndex: number): void {
    if (!this.cartData) return
    this.files[fileIndex] = item

    const index = this.getIndexOfAttachment(itemId)

    if (index >= 0) {
      if (this.cartData &&  this.cartData.Attachments &&  this.cartData.Attachments[index])
        this.cartData.Attachments[index].AttachmentData = item
    } else {
      const result = {
        OrderLineId: itemId,
        AttachmentData: item
      }
      this.cartData?.Attachments?.push(result)
    }
  }

  getIndexOfAttachment(itemId: string): number {
    if (!this.cartData) return -1
    if (!this.cartData.Attachments) {
      this.cartData.Attachments = [{
        OrderLineId: itemId,
        AttachmentData: null
      }]
      return 0
    }

    return this.cartData.Attachments.findIndex(el => el.OrderLineId ===  itemId)
  }

  next(): void {
    this.transition = 'slide-from-right'
    if (this.isStepTwo) {
      this.$v.cartData.QuotationRequestData?.$touch()
      if (this.$v.cartData.QuotationRequestData?.$invalid) {
        this.$nextTick(() => this.scrollErrorInView('step-two'))
        return
      } else {
        this.enableStepThree = true
      }
    }
    else if (this.isStepThree){
      this.$v.cartData.ProjectInformation?.$touch()
      this.$v.terms?.$touch()
      if(this.$v.cartData.ProjectInformation?.$invalid || this.$v.terms?.$invalid){
        this.$nextTick(() => this.scrollErrorInView('step-three'))
      } else {
        this.finishOrder()
      }
      return
    }

    if(!this.confirmationStep) {
      this.activeStep++
      this.sendGaCheckoutEvent(this.activeStep + 1, this.cartId, this.cartItems)
    }
    setTimeout(() => window.scrollTo(0, 0), 1000)

  }

  async changeItemQuantity(event: any, value: any, item: any, index: number): Promise<void> {
    let quantity: any = null
    let salesUnit = null

    this.setButtonLoader(index)

    if(event === 'plus') {
      salesUnit = this.getSalesUnit(item)
      quantity = this.getCorrectAmount(parseFloat(value) + salesUnit, item)

      this.plusActive = true
    } else if (event === 'minus') {
      salesUnit = this.getSalesUnit(item)
      quantity = this.getCorrectAmount(parseFloat(value) - salesUnit, item)

      this.minusActive = true
    } else {
      quantity = this.getCorrectAmount(event.target.value, item)
    }

    item.quantity = quantity

    try {
      const res = await Axios({
        method: 'put',
        url: `${BASE_URL}/cart/items`,
        data: {
          lineItemId: item.id,
          quantityDecimal: quantity
        }
      })
      if (res) {
        this.$store.commit('cart/setItemsCount', res.data.itemsCount)

        const tempItems = this.$store.state.cart.items
        tempItems.find((el: any) => {
          if (el.id ===  item.id) {
            el.quantity = quantity

            el.quantityDecimal = quantity
            return true
          }
          return false
        })
        this.$store.commit('cart/setItems', tempItems)
      } else throw new Error()
    } catch (error) {
      this.$store.commit('notifications/add', { message: this.$options?.filters?.t('changeCartAmountMessage')})
    } finally {
      this.setButtonLoader(null)
      this.minusActive = false
      this.plusActive = false
    }
  }

  async removeItem(): Promise<void> {
    const id = this.$store.state.cart.itemId
    const itemIndex = this.$store.state.cart.items.findIndex((el: any) => el.id ===  id)
    const attachmentIndex = this.cartData?.Attachments?.findIndex(el => el.OrderLineId ===  id)
    const productForGta = this.$store.state.cart.items[itemIndex]

    this.setRemoveItemLoader(itemIndex)

    try {
      const res = await Axios({
        method: 'DELETE',
        url: `${BASE_URL}/cart/items?lineItemId=${id}`
      })
      if (res) {
        this.$store.commit('cart/setItemsCount', res.data.itemsCount)
        if (attachmentIndex && attachmentIndex >= 0) {
          this.cartData?.Attachments?.splice(attachmentIndex, 1)
        }
        this.sendGaRemoveFromCartEvent(productForGta)

        const tempItems = this.$store.state.cart.items
        tempItems.splice(itemIndex, 1)
        this.$store.commit('cart/setItems', tempItems)

      } else throw new Error()
    } catch (error) {
      this.$store.commit('notifications/add', { message: this.$options?.filters?.t('deleteFromCartErrorMessage') })
    } finally {
      this.$store.commit('cart/setItemId', null)
      this.setRemoveItemLoader(null)
    }
  }

  created() {
    this.$store.commit('cart/getFromStorageNl')
    this.cartData = this.$store.state.cart.cartData
    this.$store.commit('cart/setItems', this.cartItems)
    this.itemCount = this.$store.state.cart.itemsCount

    this.$nextTick(() => this.sendGaCheckoutEvent(this.activeStep + 1, this.cartId, this.cartItems))
  }

  @Watch('cartData', { deep: true })
  onProjectInformationChange(cartData: any): void {
    this.$store.commit('cart/updateCartData', cartData)

    if(this.$v.cartData.QuotationRequestData && !this.$v.cartData.QuotationRequestData.$invalid) {
      this.enableStepThree = true
    } else {
      this.enableStepThree = false
    }

  }
}
